import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Bell, AtSign, MessageSquare } from "lucide-react";
import { useSocket } from "../context/SocketContext";

export default function NotificationsBell() {
  const { notifications, unread, markAllRead, connected } = useSocket();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) markAllRead();
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        onClick={toggle}
        data-testid="notifications-bell"
        className="relative p-2 text-[color:var(--bee-muted)] hover:text-[color:var(--bee-text)]"
        aria-label="Notifications"
      >
        <Bell size={18} />
        {unread > 0 && (
          <span
            data-testid="notifications-unread"
            className="absolute top-1 right-1 min-w-[16px] h-4 px-1 text-[10px] font-bold bg-[color:var(--bee-primary)] text-[color:var(--bee-text)] rounded-full flex items-center justify-center"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
        {connected && (
          <span className="absolute bottom-1 right-1 w-2 h-2 bg-[color:var(--bee-online)] rounded-full" title="Live" />
        )}
      </button>
      {open && (
        <div
          data-testid="notifications-panel"
          className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white border border-[color:var(--bee-border)] shadow-[6px_6px_0_0_rgba(26,29,32,0.08)] z-50"
        >
          <div className="p-3 border-b border-[color:var(--bee-border)] flex items-center justify-between">
            <span className="font-display font-semibold text-sm">Notifications</span>
            <span className="text-[10px] tracking-wider uppercase text-[color:var(--bee-muted)]">{connected ? "Live" : "Offline"}</span>
          </div>
          <div className="max-h-96 overflow-y-auto scrollbar-thin">
            {notifications.length === 0 ? (
              <div className="p-8 text-center text-sm text-[color:var(--bee-muted)]">
                <Bell size={20} className="mx-auto mb-2 opacity-40" />
                No notifications yet.
              </div>
            ) : notifications.map((n) => {
              const Icon = n.type === "mention" ? AtSign : MessageSquare;
              const actionText = n.type === "reply" ? "replied to your post" : n.type === "mention" ? "mentioned you" : "sent a notification";
              return (
                <Link
                  key={n.id}
                  to={n.link || "/"}
                  onClick={() => setOpen(false)}
                  data-testid={`notification-${n.id}`}
                  className={`block p-3 border-b border-[color:var(--bee-border)] hover:bg-[color:var(--bee-surface-alt)] ${!n.read ? "bg-[color:var(--bee-primary)]/10" : ""}`}
                >
                  <div className="flex items-start gap-2">
                    <Icon size={14} className="text-[color:var(--bee-muted)] mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs leading-snug">
                        <span className="font-semibold">{n.actor_name}</span>{" "}
                        <span className="text-[color:var(--bee-muted)]">{actionText}</span>
                      </div>
                      {n.snippet && (
                        <div className="text-xs text-[color:var(--bee-muted)] mt-1 line-clamp-2 italic">"{n.snippet}"</div>
                      )}
                      <div className="text-[10px] text-[color:var(--bee-muted)] mt-1">
                        {new Date(n.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
